<template>
  <div
    class="flex flex-col gap-4 truncate w-full justify-center"
    :class="{
      rd: bordered,
      'pa-1': bordered,
    }"
    :style="{
      border: `1px solid ${bordered ? themeVars.text3 : 'transparent'}`,
    }"
  >
    <NDynamicTags
      ref="dynamicTagsRef"
      :disabled="disabled"
      :max="maxTags"
      :value="value"
      @click.stop
      @update:value="emit('update:value', $event)"
    >
      <template #input="{ submit }">
        <NAutoComplete
          ref="autoCompleteRef"
          v-model:value="inputValue"
          :bordered="!bordered"
          :disabled="value.length >= maxTags || disabled"
          :inputProps="inputProps"
          :loading="isLoadingSuggestedTags"
          :menuProps="menuProps"
          :options="options"
          :placeholder="inputPlaceholder"
          :size="inputSameRow ? 'small' : 'medium'"
          @blur="onInputBlur"
          @click.stop
          @keydown="onInputKeydown"
          @select="onInputSelect(submit, $event)"
          @update:value="debouncedOnUpdateSearch($event)"
        />
      </template>
    </NDynamicTags>
    <div v-if="showSuggestedTags" class="flex flex-col gap-4">
      <div v-if="suggestedTagsTitle">{{ suggestedTagsTitle }}</div>
      <div class="flex flex-wrap gap-1">
        <CTag
          v-for="option in options"
          :key="option.value"
          class="cursor-pointer"
          :round="false"
          @click="addSuggestedTag(option.label)"
        >
          {{ option.label }}
        </CTag>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { debounce } from 'lodash-es'
import { NDynamicTags, NAutoComplete } from 'naive-ui'
import CTag from '@lib/components/CTag/CTag.vue'
import useThemeVars from '@lib/composables/useThemeVars'

export interface CTagsProps {
  value?: string[]
  minCharsToSuggest?: number
  suggestedTagsFetcher?: (search: string) => Promise<string[]> | string[]
  showSuggestedTags?: boolean // Note: only for static tags
  suggestedTagsTitle?: string // Note: only for static tags
  maxTags?: number
  disableCreateTag?: boolean
  disableInputOnLaunch?: boolean //Note: check in the future if we need it. If yes, then we may add trigger template to the component to modify the button
  inputSameRow?: boolean
  bordered?: boolean
  autofocus?: boolean
  minAutoCompleteWidth?: string
  disabled?: boolean
}

const props = withDefaults(defineProps<CTagsProps>(), {
  minCharsToSuggest: 3,
  maxTags: Infinity,
  value: () => [],
})

const emit = defineEmits<{
  'update:value': [value: string[]]
}>()

const { t } = useI18n()
const themeVars = useThemeVars()

const autoCompleteRef = ref<InstanceType<typeof NAutoComplete> | null>(null)
const dynamicTagsRef = ref<InstanceType<typeof NDynamicTags> | null>(null)

const isLoadingSuggestedTags = ref(false)
const suggestedTags = ref<string[]>([]) // Ref to store suggested tags
const justMounted = ref(true)
const inputValue = ref('')
const inputPlaceholder = computed(() => {
  if (props.value.length >= props.maxTags) {
    return t('canvasUI.components.tags.placeholders.maxReached')
  } else if (props.disableCreateTag) {
    return t('canvasUI.components.tags.placeholders.select') // Replace with your actual message key
  } else {
    return t('canvasUI.components.tags.placeholders.type')
  }
})
const inputProps = computed(() => {
  return {
    style: {
      'min-width': props.minAutoCompleteWidth ? props.minAutoCompleteWidth : 0,
    },
  }
})

const menuProps = computed(() => {
  return {
    style: {
      'min-width': '250px',
    },
  }
})

const options = computed(() => {
  if (!props.showSuggestedTags && (!inputValue.value || inputValue.value.length < props.minCharsToSuggest)) {
    return []
  }
  return (
    suggestedTags.value
      ?.filter((tag) => {
        const trimmedTag = tag.trim()
        const trimmedInputValue = inputValue.value.trim().toLocaleLowerCase()
        return trimmedTag.toLocaleLowerCase().includes(trimmedInputValue) && !props.value.includes(trimmedTag)
      })
      .map((tag) => ({ label: tag, value: tag })) || []
  )
})

async function showInputAndFocus() {
  await nextTick()

  dynamicTagsRef.value?.handleAddClick() //Note: it is the activate func from trigger template
  inputValue.value = ''
  autoCompleteRef.value?.focus()
}

function onInputBlur() {
  handleInputSubmit(inputValue.value)
}

function onInputSelect(submit: any, value: string) {
  submit(value)
  showInputAndFocus()
}

function onInputKeydown(e: KeyboardEvent) {
  switch (e.key) {
    case 'Enter':
    case ',':
      e.preventDefault()
      if (inputValue.value) {
        e.stopImmediatePropagation()
        handleInputSubmit(inputValue.value)
      }
      break

    case 'Backspace':
      if (!inputValue.value) {
        dynamicTagsRef.value?.handleCloseClick(props.value.length - 1)
      }
      break
  }
}

function handleInputSubmit(value: string) {
  if (props.disableCreateTag) return

  const tag = value ? value.trim() : ''
  if (!tag) return

  if (props.value.length >= props.maxTags!) return

  if (props.value.includes(tag)) return

  dynamicTagsRef.value?.handleInputConfirm(tag)

  inputValue.value = ''
  showInputAndFocus()
}

function addSuggestedTag(tag: string) {
  const newValue = [...props.value, tag]
  emit('update:value', newValue)
}

const debouncedOnUpdateSearch = debounce(onUpdateSearch, 500)

async function onUpdateSearch(value: string) {
  if (props.suggestedTagsFetcher) {
    if (value.length >= props.minCharsToSuggest) {
      isLoadingSuggestedTags.value = true
      try {
        suggestedTags.value = await props.suggestedTagsFetcher(value)
      } finally {
        isLoadingSuggestedTags.value = false
      }
    } else {
      suggestedTags.value = []
    }
  }
}

watch(autoCompleteRef, (value) => {
  if (!props.autofocus && justMounted.value) {
    justMounted.value = false

    return
  }
  if (value) nextTick(() => value.focus())
})

onMounted(async () => {
  if (!props.disableInputOnLaunch) showInputAndFocus()

  if (props.showSuggestedTags && props.suggestedTagsFetcher) {
    suggestedTags.value = await props.suggestedTagsFetcher('')
  }
})
</script>

<style lang="scss" scoped>
:deep(.n-tag__content),
:deep(.n-tag) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
:deep(.n-dynamic-tags > div:last-child) {
  flex: 1 1 v-bind('inputSameRow ? "0%" : "100%"');
}
</style>
